import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Grid,
  TextField,
  Typography,
} from '@mui/material';
import { aiToast } from './shared/ai-toast';

import { fetchWrapper } from '@/lib/fetch-wrapper';
import { urls } from '@/urls';
import ModelSelect from './shared/model-select';
import SubscriptionTreePicker from './shared/subscription-tree-picker';
import { OptimizationRunsTable } from './shared/optimization-runs';

type AiSettings = Record<string, string>;

interface RunConfig {
  model: string;
  max_findings_per_subscription: string;
  claude_timeout_ms: string;
  base_prompt: string;
}

function runConfigFromSettings(settings: AiSettings): RunConfig {
  return {
    model: settings.model ?? '',
    max_findings_per_subscription: settings.max_findings_per_subscription ?? '20',
    claude_timeout_ms: settings.claude_timeout_ms ?? '300000',
    base_prompt: settings.base_prompt ?? '',
  };
}

function OptimizationTestTab() {
  const [settings, setSettings] = useState<AiSettings | null>(null);
  const [selectedSubs, setSelectedSubs] = useState<string[]>([]);
  const [runConfig, setRunConfig] = useState<RunConfig | null>(null);
  const [dryRun, setDryRun] = useState(false);
  const [triggering, setTriggering] = useState(false);

  const load = useCallback(async () => {
    const settingsData = await fetchWrapper.get(urls.DevOps.aiEngineSettings);
    setSettings(settingsData);
    setRunConfig(runConfigFromSettings(settingsData));
  }, []);

  useEffect(() => {
    load().catch(() => aiToast.error('Failed to load AI optimization settings'));
  }, [load]);

  const resetRunConfig = () => {
    if (settings) {
      setRunConfig(runConfigFromSettings(settings));
    }
  };

  const setRunField = (key: keyof RunConfig, value: string) => {
    setRunConfig((prev) => (prev ? { ...prev, [key]: value } : prev));
  };

  const triggerRun = async () => {
    if (selectedSubs.length === 0) {
      aiToast.error('Select at least one subscription');
      return;
    }
    if (!runConfig) return;

    setTriggering(true);
    try {
      const body: Record<string, unknown> = {
        dry_run: dryRun,
        subscription_ids: selectedSubs,
        config_override: runConfig,
      };
      const res = await fetchWrapper.post(urls.DevOps.aiEngineTriggerRun, body);
      aiToast.success(res.message ?? 'Analysis started');
    } catch (error: any) {
      aiToast.error(error?.message ?? 'Failed to start analysis');
    } finally {
      setTriggering(false);
    }
  };

  if (!settings || !runConfig) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Typography variant="h6">Optimization — test runs</Typography>
        <Typography variant="body2" color="text.secondary">
          Trigger analysis manually for selected subscriptions. Run settings
          apply to this run only and are not saved to the scheduled
          configuration.
        </Typography>
      </Grid>

      <Grid item xs={12}>
        <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
          Subscriptions
        </Typography>
        <SubscriptionTreePicker
          selected={selectedSubs}
          onChange={setSelectedSubs}
        />
      </Grid>

      <Grid item xs={12}>
        <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
          Run configuration
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <ModelSelect
              value={runConfig.model}
              onChange={(v) => setRunField('model', v)}
              testId="ai-run-model"
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              label="Max findings per subscription"
              type="number"
              value={runConfig.max_findings_per_subscription}
              onChange={(e) =>
                setRunField('max_findings_per_subscription', e.target.value)
              }
              data-testid="ai-run-max-findings"
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              label="Claude timeout (ms)"
              type="number"
              value={runConfig.claude_timeout_ms}
              onChange={(e) => setRunField('claude_timeout_ms', e.target.value)}
              data-testid="ai-run-timeout"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              minRows={6}
              label="Analysis prompt"
              value={runConfig.base_prompt}
              onChange={(e) => setRunField('base_prompt', e.target.value)}
              data-testid="ai-run-prompt"
            />
          </Grid>
        </Grid>

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 2 }}>
          <Button
            size="small"
            variant="text"
            onClick={resetRunConfig}
            data-testid="ai-run-reset-config">
            Reset run config from scheduled defaults
          </Button>
        </Box>

        <FormControlLabel
          sx={{ mt: 1, display: 'block' }}
          control={
            <Checkbox
              checked={dryRun}
              onChange={(e) => setDryRun(e.target.checked)}
              data-testid="ai-run-dry-run"
            />
          }
          label="Dry run (no writes)"
        />
        <Box sx={{ mt: 2 }}>
          <Button
            variant="outlined"
            onClick={triggerRun}
            disabled={triggering || selectedSubs.length === 0}
            data-testid="ai-engine-run-now">
            {triggering ? 'Starting…' : 'Run analysis now'}
          </Button>
        </Box>
      </Grid>

      <Grid item xs={12}>
        <OptimizationRunsTable
          triggerFilter="manual"
          title="Manual run history"
          testId="ai-manual-batch-runs"
        />
      </Grid>
    </Grid>
  );
}

export default OptimizationTestTab;
